import { useEffect, useMemo, type ComponentProps, type FC } from 'react';

import moment, { type Moment } from 'moment';
import { useIntl } from 'react-intl';

import { DatePicker, Drawer, Form, Input, Radio, Select } from 'antd';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import { FORM_LAYOUT_DEFAULT } from '@src/constants/commons';
import { ABSENCE_TYPES } from '@src/containers/App/TimeTracker/Main/components/reports/shared/constants';
import type { IWorkJourneyMatrixUser } from '@src/models/interfaces/workJourneyManagement.interface';
import { JUSTIFIED_ABSENCE_REASON_CODES } from '@src/packages/absences/constants';
import IntlMessages from '@src/util/IntlMessages';

import { buildMatrixAbsenceSubmitPayload } from '../../utils/buildMatrixAbsenceSubmitPayload';
import { formatMatrixExceptionRangeLabel } from '../../utils/formatMatrixExceptionRangeLabel';
import type { MatrixExceptionDateRange } from '../../utils/resolveMatrixExceptionDateRanges';
import drawerStyles from '../matrixSelectionDrawers.module.less';

const CLARIFICATIONS_MAX = 100;

export type MatrixAbsenceExcuse = 'justified_absence' | 'unjustified_absence';

export type WorkJourneyMatrixAbsenceDrawerProps = {
  open: boolean;
  user: IWorkJourneyMatrixUser | null;
  initialExcuse: MatrixAbsenceExcuse;
  initialStartDate: string;
  initialEndDate: string;
  /**
   * Hides the editable period controls (RealTime single-day, or matrix gapped
   * multi-exception selection). Existing callers keep working with `hidePeriod`
   * alone; pass `selectionRanges` to show the ranges that will be created.
   */
  hidePeriod?: boolean;
  /** Contiguous gaps → one chip per exception range that submit will create. */
  selectionRanges?: MatrixExceptionDateRange[];
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    excuse: MatrixAbsenceExcuse;
    reasonType: string | null;
    startDate: string;
    endDate: string;
    description: string;
  }) => Promise<void> | void;
};

type AbsenceFormValues = {
  durationMode: 'single' | 'range';
  startDate: Moment | null;
  endDate: Moment | null;
  excuse: MatrixAbsenceExcuse;
  reasonType: string | undefined;
  description: string;
};

export const WorkJourneyMatrixAbsenceDrawer: FC<WorkJourneyMatrixAbsenceDrawerProps> = ({
  open,
  user,
  initialExcuse,
  initialStartDate,
  initialEndDate,
  hidePeriod = false,
  selectionRanges,
  submitting = false,
  onClose,
  onSubmit,
}) => {
  const intl = useIntl();
  const [form] = Form.useForm<AbsenceFormValues>();
  const durationMode = Form.useWatch('durationMode', form);
  const excuse = Form.useWatch('excuse', form);
  const description = Form.useWatch('description', form) ?? '';
  const locale = intl.locale === 'es' ? 'es-CL' : 'en-US';
  const showSelectionRanges = Boolean(hidePeriod && selectionRanges && selectionRanges.length > 0);

  const title = useMemo(
    () =>
      intl.formatMessage(
        { id: 'workJourney.management.matrix.absenceDrawer.title' },
        { name: user?.displayName ?? '—' },
      ),
    [intl, user?.displayName],
  );

  useEffect(() => {
    if (!open) return;
    const start = moment(initialStartDate, 'YYYY-MM-DD');
    const end = moment(initialEndDate, 'YYYY-MM-DD');
    const isSingle = initialStartDate === initialEndDate;
    form.setFieldsValue({
      durationMode: isSingle ? 'single' : 'range',
      startDate: start.isValid() ? start : null,
      endDate: end.isValid() ? end : null,
      excuse: initialExcuse,
      reasonType: undefined,
      description: '',
    });
  }, [form, initialEndDate, initialExcuse, initialStartDate, open]);

  const handleFinish = async (values: AbsenceFormValues) => {
    const payload = buildMatrixAbsenceSubmitPayload({
      excuse: values.excuse,
      reasonType: values.reasonType,
      startDate: values.startDate?.format('YYYY-MM-DD') || '',
      endDate: values.endDate?.format('YYYY-MM-DD') || '',
      durationMode: values.durationMode,
      description: values.description,
    });
    if (!payload) return;
    await onSubmit(payload);
  };

  return (
    <Drawer
      open={open}
      width={560}
      title={null}
      closable={false}
      footer={null}
      destroyOnClose
      onClose={onClose}
    >
      <BoxContainer>
        <BoxContainer content fixed bottomBorder>
          <FilterContainer
            goBack={onClose}
            title={<Title.Header value={title} />}
            actionButtons={[
              {
                label: intl.formatMessage({
                  id: 'workJourney.management.matrix.absenceDrawer.register',
                }),
                type: 'primary',
                action: () => form.submit(),
                disabled: submitting || !user,
              },
            ]}
          />
        </BoxContainer>

        <BoxContainer content>
          <div className={drawerStyles.infoCard}>
            <span className={drawerStyles.infoRowLabel}>
              <IntlMessages id="workJourney.management.matrix.absenceDrawer.user" />
            </span>
            <span className={drawerStyles.infoRowValue}>{user?.displayName || '—'}</span>
            <span className={drawerStyles.infoRowLabel}>
              <IntlMessages id="workJourney.management.matrix.absenceDrawer.idCard" />
            </span>
            <span className={drawerStyles.infoRowValue}>{user?.idCard || '—'}</span>
            <span className={drawerStyles.infoRowLabel}>
              <IntlMessages id="workJourney.management.matrix.absenceDrawer.marking" />
            </span>
            <span className={drawerStyles.infoRowValue}>
              <IntlMessages id="workJourney.management.matrix.absenceDrawer.markingAbsence" />
            </span>
          </div>
          <Form
            form={form}
            layout={FORM_LAYOUT_DEFAULT as unknown as ComponentProps<typeof Form>['layout']}
            onFinish={handleFinish}
            initialValues={{
              durationMode: 'range',
              excuse: 'justified_absence',
              description: '',
            }}
          >
            {!hidePeriod && (
              <>
                <p className={drawerStyles.sectionTitle}>
                  <IntlMessages id="workJourney.management.matrix.absenceDrawer.periodSection" />
                </p>
                <Form.Item name="durationMode">
                  <Radio.Group>
                    <Radio value="single">
                      <IntlMessages id="workJourney.management.cover.replace.step2.duration.single" />
                    </Radio>
                    <Radio value="range">
                      <IntlMessages id="workJourney.management.cover.replace.step2.duration.range" />
                    </Radio>
                  </Radio.Group>
                </Form.Item>

                <div className={drawerStyles.dateRow}>
                  <Form.Item
                    name="startDate"
                    label={intl.formatMessage({
                      id: 'workJourney.management.matrix.absenceDrawer.startDate',
                    })}
                    rules={[{ required: true, message: intl.formatMessage({ id: 'form.required' }) }]}
                  >
                    <DatePicker className="gx-w-100" format="DD/MM/YY" />
                  </Form.Item>
                  {durationMode !== 'single' && (
                    <Form.Item
                      name="endDate"
                      label={intl.formatMessage({
                        id: 'workJourney.management.matrix.absenceDrawer.endDate',
                      })}
                      rules={[{ required: true, message: intl.formatMessage({ id: 'form.required' }) }]}
                    >
                      <DatePicker className="gx-w-100" format="DD/MM/YY" />
                    </Form.Item>
                  )}
                </div>
              </>
            )}
            {hidePeriod && (
              <>
                {showSelectionRanges && (
                  <>
                    <p className={drawerStyles.sectionTitle}>
                      <IntlMessages id="workJourney.management.matrix.absenceDrawer.periodsSection" />
                    </p>
                    <ul className={drawerStyles.selectionRanges}>
                      {selectionRanges!.map(range => (
                        <li
                          key={`${range.startDate}_${range.endDate}`}
                          className={drawerStyles.selectionRangeChip}
                        >
                          {formatMatrixExceptionRangeLabel(range, locale)}
                        </li>
                      ))}
                    </ul>
                    <p className={drawerStyles.selectionRangesHint}>
                      <IntlMessages
                        id="workJourney.management.matrix.absenceDrawer.multipleExceptionsHint"
                        values={{ count: selectionRanges!.length }}
                      />
                    </p>
                  </>
                )}
                <Form.Item name="durationMode" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="startDate" hidden>
                  <DatePicker />
                </Form.Item>
                <Form.Item name="endDate" hidden>
                  <DatePicker />
                </Form.Item>
              </>
            )}

            <p className={drawerStyles.sectionTitle}>
              <IntlMessages id="workJourney.management.matrix.absenceDrawer.typeSection" />
            </p>
            <Form.Item name="excuse">
              <Radio.Group>
                <Radio value="justified_absence">
                  <IntlMessages id="workJourney.management.matrix.selectionBar.justifiedAbsence" />
                </Radio>
                <Radio value="unjustified_absence">
                  <IntlMessages id="workJourney.management.matrix.selectionBar.unjustifiedAbsence" />
                </Radio>
              </Radio.Group>
            </Form.Item>

            {excuse === 'justified_absence' && (
              <Form.Item
                name="reasonType"
                label={intl.formatMessage({
                  id: 'workJourney.management.matrix.absenceDrawer.reason',
                })}
                rules={[{ required: true, message: intl.formatMessage({ id: 'form.required' }) }]}
              >
                <Select
                  showSearch
                  optionFilterProp="children"
                  placeholder={intl.formatMessage({
                    id: 'workJourney.management.matrix.absenceDrawer.reasonPlaceholder',
                  })}
                >
                  {ABSENCE_TYPES.filter(item =>
                    JUSTIFIED_ABSENCE_REASON_CODES.includes(item.code),
                  ).map(item => (
                    <Select.Option key={item.code} value={item.code}>
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <p className={drawerStyles.sectionTitle}>
              <IntlMessages id="workJourney.management.matrix.absenceDrawer.clarifications" />
            </p>
            <div className={drawerStyles.clarificationsWrap}>
              <Form.Item name="description" className="gx-mb-0">
                <Input.TextArea
                  rows={4}
                  maxLength={CLARIFICATIONS_MAX}
                  placeholder={intl.formatMessage({
                    id: 'workJourney.management.matrix.absenceDrawer.clarificationsPlaceholder',
                  })}
                />
              </Form.Item>
              <span className={drawerStyles.charCount}>
                {String(description).length} / {CLARIFICATIONS_MAX}
              </span>
            </div>
          </Form>
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};
